import React from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../../components/SeoHead'
import { COMPANY, FUNDAMENT_FAQ, IMAGES } from '../../data/siteData'
import { PAGE_META } from '../../data/metaData'
import { getOptimizedImageUrl } from '../../lib/imageHelper'
import ContactForm from '../../components/ContactForm'
import GallerySection from '../../components/GallerySection'
import FaqAccordion from '../../components/FaqAccordion'

export default function FundamentPage() {
  const metaTitle = PAGE_META.fundament.title
  const metaDescription = PAGE_META.fundament.description

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FUNDAMENT_FAQ.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }

  return (
    <div>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        schemaData={faqSchema}
        canonicalPath="/fundament"
      />

      {/* Hero */}
      <section className="relative bg-stone-950 text-white overflow-hidden py-16 sm:py-24 border-b border-stone-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(IMAGES.fundament.hero, { width: 1800, quality: 80 })}
            alt="Gul CAT gravemaskine ved udgravning af rende til fundament og sokkel"
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/50 to-stone-950/20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs sm:text-sm font-semibold mb-4">
              <i className="fa-solid fa-cubes-stacked" aria-hidden="true"></i>
              <span>Støbning & fundamentopbygning</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight drop-shadow-md">
              Et solidt fundament til dit byggeprojekt
            </h1>

            <p className="mt-5 text-base sm:text-xl text-stone-200 leading-relaxed max-w-2xl drop-shadow-sm">
              Et stærkt fundament er afgørende for, at dit byggeri står stabilt og holder i mange år. Hos <Link to="/" className="text-white font-semibold underline decoration-amber-500 decoration-2 underline-offset-2 hover:text-amber-400 transition-colors">JT Knudsen</Link> hjælper vi med fundament opbygning til blandt andet tilbygninger, renoveringer og specialiserede byggeopgaver i Østjylland og på Djursland. Vi tilpasser arbejdet til projektet og forholdene på grunden.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
              <a
                href="#kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-lg bg-[#015205] hover:bg-[#026b07] text-white font-bold text-base shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <span>Få tilbud på fundament</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </a>

              <a
                href={COMPANY.phoneHref}
                className="inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-lg bg-stone-900 hover:bg-stone-800 text-stone-100 font-bold text-base border border-stone-700 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-amber-400" aria-hidden="true"></i>
                <span>Ring på {COMPANY.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Opbygning af fundament begynder med de rigtige valg */}
      <section className="py-14 sm:py-20 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Grundighed & Stabilitet
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-stone-900">
                Opbygning af fundament begynder med de rigtige valg
              </h2>
              <div className="text-stone-700 space-y-4 text-base sm:text-lg leading-relaxed">
                <p>
                  Et fundament skal overføre belastningen fra byggeriet til jorden uden unødige bevægelser. Derfor handler opbygning af fundament både om jordbund, konstruktion, dybde og materialer. Når disse forhold tænkes sammen fra starten, mindskes risikoen for sætninger, revner og fugtrelaterede problemer.
                </p>
                <p>
                  Vi tager udgangspunkt i tegningerne, byggeriets størrelse og de konkrete forhold på stedet. Adgangsforhold, niveauforskelle og behovet for bortkørsel af jord kan også påvirke løsningen. På den måde bliver arbejdet planlagt praktisk, og fundamentet får den opbygning, som opgaven kræver.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="p-3 rounded-lg bg-white border border-stone-200 flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-amber-500" aria-hidden="true"></i>
                  <span className="text-sm font-semibold text-stone-800">Præcis afsætning af byggefeltet</span>
                </div>
                <div className="p-3 rounded-lg bg-white border border-stone-200 flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-amber-500" aria-hidden="true"></i>
                  <span className="text-sm font-semibold text-stone-800">Udgravning i nødvendig dybde</span>
                </div>
                <div className="p-3 rounded-lg bg-white border border-stone-200 flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-amber-500" aria-hidden="true"></i>
                  <span className="text-sm font-semibold text-stone-800">Bærelag, isolering & armering</span>
                </div>
                <div className="p-3 rounded-lg bg-white border border-stone-200 flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-amber-500" aria-hidden="true"></i>
                  <span className="text-sm font-semibold text-stone-800">Forberedelse til betonstøbning</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-200">
                <img
                  src={getOptimizedImageUrl(IMAGES.fundament.casting, { width: 800, height: 600 })}
                  alt="Nystøbt betonfundament med opretstående armeringsjern og træafstivning i udgravning"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process & Støbning */}
      <section className="py-16 sm:py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
                Støbearbejdet
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Fra udgravning til støbning af fundament
              </h2>
              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Selve forløbet varierer efter projektets omfang og den valgte konstruktion. Normalt starter arbejdet med afsætning og udgravning, hvorefter bunden klargøres. Der tages højde for højder og overgange, så fundament og den efterfølgende konstruktion passer præcist sammen.
              </p>
              <p className="text-stone-300 text-base leading-relaxed">
                Ved støbning af fundament er forberedelsen mindst lige så vigtig som betonen. Underlaget skal være stabilt, og forskalling samt armering skal tilpasses løsningen. Den præcise udførelse afhænger blandt andet af belastning, jordbund og projektets tekniske materiale.
              </p>
              <p className="text-stone-400 text-sm leading-relaxed">
                Vi holder fokus på et ryddeligt og sammenhængende forløb, hvor de enkelte trin udføres i den rigtige rækkefølge. Har du andre bygge- eller anlægsopgaver omkring projektet, ser vi gerne på, hvordan de kan planlægges samlet. Det gælder for eksempel efterfølgende <Link to="/belaegning" className="text-amber-400 hover:text-amber-300 font-semibold underline underline-offset-2">belægning</Link> af fliseterrasse, gangstier eller indkørsel omkring nybyggeriet, eller hvis haven samtidig skal optimeres med en professionel <Link to="/nedgravning-af-trampolin" className="text-amber-400 hover:text-amber-300 font-semibold underline underline-offset-2">nedgravning af trampolin</Link>.
              </p>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-stone-950/80 border border-stone-800 rounded-2xl p-7 sm:p-9 space-y-4">
                <h3 className="text-lg font-bold text-white border-b border-stone-800 pb-3">
                  Kvalitetskontrol i støbeprocessen:
                </h3>
                <ul className="space-y-3.5 text-stone-300 text-sm sm:text-base">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 text-xs mt-0.5">
                      1
                    </span>
                    <span>Kontrol af mål og niveauer før støbning</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 text-xs mt-0.5">
                      2
                    </span>
                    <span>Placering af forskalling og relevant armering</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 text-xs mt-0.5">
                      3
                    </span>
                    <span>Støbning og omhyggelig fordeling af betonen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 text-xs mt-0.5">
                      4
                    </span>
                    <span>Kontrol før det videre byggeri fortsætter</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions & Helhed */}
      <section className="py-16 sm:py-20 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-white p-7 sm:p-9 rounded-2xl border border-stone-200 shadow-sm space-y-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center text-xl">
                <i className="fa-solid fa-shapes" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Hvilken løsning passer til dit byggeri?
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Der findes ikke ét fundament, som passer til alle byggerier. En mindre konstruktion kan have andre krav end en tilbygning, og både randfundament, punktfundament eller en samlet betonplade kan være relevant. Valget skal altid tage udgangspunkt i konstruktionen og de faktiske jordforhold.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Er du i tvivl om fundamentets opbygning, hjælper vi med at skabe overblik over den praktiske udførelse. Vi ser på, hvad der skal bygges, hvordan området kan tilgås, og hvilke arbejdsprocesser der er nødvendige. Det giver et bedre grundlag for at vælge en holdbar løsning.
              </p>
            </div>

            <div className="bg-white p-7 sm:p-9 rounded-2xl border border-stone-200 shadow-sm space-y-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center text-xl">
                <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Fundament og sokkel skal fungere som en helhed
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Fundamentet bærer byggeriet, mens soklen danner overgangen mellem jorden og bygningen. Korrekte højder, en stabil opbygning og hensyn til fugt er derfor vigtige. Når fundament og sokkel tilpasses hinanden, skabes et bedre udgangspunkt for resten af byggeriet.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Vi har mange års praktisk erfaring med præcist forarbejde og sikrer, at afsætningen og udgravningen stemmer overens med bygningens moduler og krav.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Price & Local Presence */}
      <section className="py-16 sm:py-20 bg-stone-100 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="bg-white p-7 sm:p-9 rounded-2xl border border-stone-200 shadow-sm space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Pris & Beregning
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Hvad koster et nyt fundament?
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Prisen afhænger blandt andet af fundamentets størrelse, udgravningsdybden, jordbundsforholdene og adgangen for maskiner. Materialevalg, armering, isolering og mængden af jord, der skal håndteres, har også betydning. Derfor vurderes opgaven bedst ud fra det konkrete projekt.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Når vi kender dine planer og forholdene på adressen, kan vi tage en konkret dialog om opgaven. Du får afklaret, hvad arbejdet omfatter, og hvilke forhold der kan påvirke forløbet. Det gør det lettere at sammenholde løsningen med dit budget og din tidsplan.
              </p>
            </div>

            <div className="bg-white p-7 sm:p-9 rounded-2xl border border-stone-200 shadow-sm space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Lokal service
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Få lokal hjælp i Østjylland og på Djursland
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                JT Knudsen holder til i Hjortshøj og kører ud til fundamentopgaver i både Østjylland og på Djursland. Vi hjælper blandt andet kunder i Aarhus, Egå, Hornslet, Hinnerup og Grenaa. Den lokale placering gør det enkelt at tage en dialog om både små og større byggeopgaver.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Skal du bruge et solidt fundament til en tilbygning, renovering eller specialiseret byggeopgave, er du velkommen til at kontakte os. Fortæl kort om dine planer, projektets placering og det forventede omfang. Så kan vi tale om den rette opbygning af fundament og næste skridt i processen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-stone-50 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GallerySection
            galleryKey="fundament-sokkel"
            title="Billeder af fundament- og sokkelarbejde"
            subtitle="Se udgravning i frostfri dybde, klargjort armering og støbning"
          />
        </div>
      </section>

      {/* FAQ */}
      <FaqAccordion
        items={FUNDAMENT_FAQ}
        title="Spørgsmål om opbygning af fundament"
        subtitle="Vigtige overvejelser om dybde, jordbund, armering og sokkelhøjder"
      />

      {/* Contact */}
      <section className="py-16 sm:py-24 bg-stone-950 text-white border-t border-stone-800" id="kontakt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
                Uforpligtende tilbud
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Skal vi hjælpe med dit næste fundament?
              </h2>
              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Send os en beskrivelse eller ring direkte til Jonas. Vi gennemgår dit projekt og leverer en holdbar løsning, der passer præcist til konstruktionen.
              </p>

              <div className="p-5 rounded-xl bg-stone-900 border border-stone-800 space-y-3 text-sm">
                <div className="font-semibold text-stone-200">Direkte kontakt:</div>
                <div className="flex items-center gap-2 text-stone-300">
                  <i className="fa-solid fa-phone text-amber-400" aria-hidden="true"></i>
                  <a href={COMPANY.phoneHref} className="hover:text-amber-400 font-bold whitespace-nowrap">
                    {COMPANY.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-stone-300">
                  <i className="fa-solid fa-envelope text-amber-400" aria-hidden="true"></i>
                  <a href={`mailto:${COMPANY.email}`} className="hover:text-amber-400 whitespace-nowrap break-keep">
                    {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm defaultService="Fundament & sokkel" />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
